/**
 * Remembers guest requester fields per wizard search request id (session tab).
 * Avoids retyping and skips redundant PATCH when contacting another business with the same search doc.
 */

export const WIZARD_REQUESTER_CACHE_KEY_PREFIX = 'nky_wizard_requester_v1';

/** Cache slot holding the last entry written for any request (prefill only). */
const LATEST_ENTRY_ID = 'latest';

function key(requestId) {
  return `${WIZARD_REQUESTER_CACHE_KEY_PREFIX}:${requestId}`;
}

/** Removes all guest requester-form cache entries for this tab (e.g. on logout). */
export function clearAllWizardRequesterSessionCaches() {
  try {
    const prefix = `${WIZARD_REQUESTER_CACHE_KEY_PREFIX}:`;
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * @typedef {Object} WizardRequesterCacheEntry
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} phoneInput Raw phone as entered in the form (for display)
 * @property {{ firstName: string, lastName: string, email: string, phone?: string }} savedPatchFingerprint Last successful PATCH body shape (phone = digits if present)
 */

/**
 * @param {string} requestId
 * @returns {WizardRequesterCacheEntry | null}
 */
export function readWizardRequesterCache(requestId) {
  if (!requestId) return null;
  try {
    const raw = sessionStorage.getItem(key(requestId));
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j || typeof j !== 'object') return null;
    return j;
  } catch {
    return null;
  }
}

/**
 * @param {string} requestId
 * @param {WizardRequesterCacheEntry} entry
 */
export function writeWizardRequesterCache(requestId, entry) {
  if (!requestId || !entry) return;
  try {
    sessionStorage.setItem(key(requestId), JSON.stringify(entry));
    sessionStorage.setItem(key(LATEST_ENTRY_ID), JSON.stringify(entry));
  } catch {
    // quota / private mode
  }
}

/**
 * Most recently saved guest contact fields in this tab, for prefilling a form whose search
 * request does not exist yet. Never use its `savedPatchFingerprint` to skip a PATCH.
 * @returns {WizardRequesterCacheEntry | null}
 */
export function readLatestWizardRequesterCache() {
  return readWizardRequesterCache(LATEST_ENTRY_ID);
}

/**
 * @param {object} a PATCH body (no clientSessionId required for compare)
 * @param {object} b fingerprint from cache
 */
export function wizardRequesterPatchMatchesSaved(a, b) {
  if (!a || !b) return false;
  return (
    (a.firstName || '') === (b.firstName || '') &&
    (a.lastName || '') === (b.lastName || '') &&
    (a.email || '') === (b.email || '') &&
    String(a.phone || '') === String(b.phone || '')
  );
}
